import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgApexchartsModule } from 'ng-apexcharts';
import { DashboardService } from '../../services/dashboard.service';
import { DashboardFilters } from '../../components/filters/dashboard-filters';
import { BusinessStateService } from '@core/services/business-state.service';
import { Subject, takeUntil, forkJoin } from 'rxjs';
import { ResultsApi } from '@features/results/services/results-api';

const DASHBOARD_COLORS = ['#075902','#211261', '#2d8afb'];

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, NgApexchartsModule, DashboardFilters],
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.scss']
})
export class Dashboard implements OnInit, OnDestroy {

  private svc = inject(DashboardService);
  private resultsApi = inject(ResultsApi);
  private businessStateService = inject(BusinessStateService);
  private destroy$ = new Subject<void>();

  donutOptions: any = {};
  stackedOptions: any = {};
  horizontalOptions: any = {};
  officeOptions: any = {};
  monthlyOptions: any = {};

  hasDonutData = false;
  hasStackedData = false;
  hasHorizontalData = false;
  hasOfficeData = false;
  hasMonthlyData = false;

  loading = false;
  errorMessage: string | null = null;
  currentMode: 'alcance' | 'categoria' = 'alcance';

  selectedBusinessType: string = 'all';
  selectedBusinessId: string | null = null;
  selectedOfficeId: string | null = null;

  ngOnInit(): void {
    this.businessStateService.selectedBusiness$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selected => {
        if (selected?.data) {
          this.loadOverview({
            year: new Date().getFullYear(),
            pcg: 'AR6',
            mode: this.currentMode
          });
        }
      });

    this.loadOverview({
      year: new Date().getFullYear(),
      pcg: 'AR6',
      mode: 'alcance'
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFiltersApply(filtro: any) {
    const req: Record<string, any> = {};

    const year = Number(filtro.year || 0);
    if (!year || year <= 0) {
      this.errorMessage = 'Seleccione un año válido.';
      return;
    }
    req['year'] = year;
    req['pcg'] = filtro.pcg ?? 'AR6';
    req['mode'] = filtro.mode ?? 'alcance';

    this.currentMode = filtro.mode ?? 'alcance';

    if (filtro.variations && filtro.variations !== 'Todas') {
      req['variation'] = filtro.variations;
    }

    if (filtro.mode === 'categoria' && filtro.alcance && filtro.alcance !== 'Todas') {
      const categoryValue = Number(filtro.alcance);
      if (!isNaN(categoryValue) && categoryValue >= 0) {
        req['category'] = categoryValue;
      }
    } else if (filtro.mode === 'alcance' && filtro.alcance && filtro.alcance !== 'Todas') {
      const scopeValue = Number(filtro.alcance);
      if (!isNaN(scopeValue) && scopeValue >= 0) {
        req['scope'] = scopeValue;
      }
    }

    this.errorMessage = null;
    this.loadOverview(req);
  }

  get showDonutChart(): boolean {
    return true;
  }

  get showStackedChart(): boolean {
    return this.selectedBusinessType === 'all' || this.selectedBusinessType === 'Group';
  }

  get showHorizontalChart(): boolean {
    return true;
  }

  get showOfficeChart(): boolean {
    return this.selectedBusinessType === 'Business' ||
           this.selectedBusinessType === 'all' ||
           this.selectedBusinessType === 'Group';
  }

  get showMonthlyChart(): boolean {
    return true;
  }

  loadOverview(filters?: any) {
    this.loading = true;

    if (!filters) filters = {};

    // Obtener scope actualizado
    const selectedBusiness = this.businessStateService.getSelectedBusiness();

    if (selectedBusiness?.data?.id) {
      const { kind, id } = selectedBusiness.data;
      this.selectedBusinessType = kind || 'all';

      // Si el scope es "Todos" no se envía ni businessId ni officeId
      if (id !== 'all') {
        if (kind === 'Group' || kind === 'Business') {
          filters['businessId'] = id;
          this.selectedBusinessId = id;
        }
        else if (kind === 'Office') {
          filters['officeId'] = id;
          this.selectedOfficeId = id;
        }
      }
    }

    const monthlyParams: Record<string, any> = {};
    if (filters['year']) monthlyParams['years'] = [filters['year']];
    if (filters['pcg']) monthlyParams['gwp'] = filters['pcg'];
    if (filters['businessId'] && filters['businessId'] !== 'Todas') monthlyParams['businessId'] = filters['businessId'];
    if (filters['officeId'] && filters['officeId'] !== 'Todas') monthlyParams['officeId'] = filters['officeId'];
    if (filters['variation'] && filters['variation'] !== 'Todas') monthlyParams['variation'] = filters['variation'];
    if (filters['category'] !== undefined && filters['category'] !== null) monthlyParams['categories'] = [filters['category']];
    if (filters['scope'] !== undefined && filters['scope'] !== null) monthlyParams['scopes'] = [filters['scope']];

    forkJoin({
      overview: this.svc.getOverview(filters),
      monthlyResp: this.resultsApi.getMonthlyEmissions(monthlyParams)
    }).subscribe({
      next: ({ overview: resp, monthlyResp }) => {
        this.currentMode = resp.mode || 'alcance';

        const donut = this.svc.getDonutSeries(resp);
        const stacked = this.svc.getStackedByCompany(resp);
        const monthly = this.svc.getMonthlySeries(monthlyResp);

        const variations = this.svc.getVariationHorizontal(resp);
        const office = this.svc.getOfficeHorizontal(resp);

        this.hasDonutData = donut.series && donut.series.length > 0 &&
          donut.series.some((v: number) => v > 0);

        this.hasStackedData = stacked.series && stacked.series.length > 0 &&
          stacked.series.some((s: any) => s.data && s.data.some((v: number) => v > 0));

        this.hasHorizontalData = variations.series && variations.series.length > 0 &&
          variations.series.some((s: any) => s.data && s.data.some((v: number) => v > 0));

        this.hasOfficeData = office.series && office.series.length > 0 &&
          office.series.some((s: any) => s.data && s.data.some((v: number) => v > 0));

        this.hasMonthlyData = monthly.series && monthly.series.length > 0 &&
          monthly.series.some((s: any) => s.data && s.data.some((v: number) => v > 0));

        const donutColors = (donut.series || []).map((_: any, i: number) =>
          DASHBOARD_COLORS[i % DASHBOARD_COLORS.length]
        );

        this.donutOptions = {
          series: donut.series,
          labels: donut.labels,
          colors: donutColors,
          fill: { colors: donutColors, opacity: 1 },
          stroke: { colors: ['#ffffff'], width: 1 },
          chart: { type: 'donut', height: 260, toolbar: { show: false } },
          plotOptions: { pie: { donut: { size: '65%' } } },
          legend: { position: 'right' },
          dataLabels: {
            enabled: true,
            style: { colors: ['#fff'] },
            formatter: (val: number) => `${val.toFixed(1)}%`
          },
          tooltip: {
            y: {
              formatter: (val: number) => {
                return `${val.toLocaleString('de-DE', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })} tCO2e`;
              }
            }
          }
        };

        const stackedSeriesWithColors = (stacked.series || []).map((s: any, i: number) => ({
          ...s,
          color: DASHBOARD_COLORS[i % DASHBOARD_COLORS.length]
        }));

        const stackedColors = (stacked.series || []).map((_: any, i: number) =>
          DASHBOARD_COLORS[i % DASHBOARD_COLORS.length]
        );

        const variationsHeight = Math.max(360, variations.categories.length * 23);
        this.horizontalOptions = {
          series: variations.series,
          colors: DASHBOARD_COLORS,
          fill: { colors: DASHBOARD_COLORS, opacity: 1 },
          chart: { type: 'bar', height: variationsHeight, toolbar: { show: false } },
          plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 4 } },
          xaxis: {
            categories: variations.categories,
            title: {
              text: 'tCO2e',
              style: { color: '#747677ff', fontSize: '12px', fontWeight: 600 }
            },
            labels: {
              formatter: (val: string) => {
                const num = Number(val);
                return num.toLocaleString('de-DE', { 
                  minimumFractionDigits: 0, 
                  maximumFractionDigits: 1 
                });
              }
            }
          },
          dataLabels: {
            enabled: true,
            formatter: (val: number) => {
              return val.toLocaleString('de-DE', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              });
            },
            style: { colors: ['#fff'], fontSize: '11px', fontWeight: 600 }
          },
          tooltip: {
            y: {
              formatter: (val: number) => {
                return `${val.toLocaleString('de-DE', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })} tCO2e`;
              }
            }
          }
        };

        const officeHeight = Math.max(360, office.categories.length * 23);
        this.officeOptions = {
          series: office.series,
          colors: DASHBOARD_COLORS,
          fill: { colors: DASHBOARD_COLORS, opacity: 1 },
          chart: { type: 'bar', height: officeHeight, toolbar: { show: false } },
          plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 4 } },
          xaxis: {
            categories: office.categories,
            title: {
              text: 'tCO2e',
              style: { color: '#747677ff', fontSize: '12px', fontWeight: 600 }
            },
            labels: {
              formatter: (val: string) => {
                const num = Number(val);
                return num.toLocaleString('de-DE', { 
                  minimumFractionDigits: 0, 
                  maximumFractionDigits: 1 
                });
              }
            }
          },
          dataLabels: {
            enabled: true,
            formatter: (val: number) => {
              return val.toLocaleString('de-DE', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              });
            },
            style: { colors: ['#fff'], fontSize: '10px', fontWeight: 600 }
          },
          tooltip: {
            y: {
              formatter: (val: number) => {
                return `${val.toLocaleString('de-DE', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })} tCO2e`;
              },
            }
          }
        };

        this.stackedOptions = {
          series: stackedSeriesWithColors,
          colors: stackedColors,
          fill: { colors: stackedColors, opacity: 1 },
          chart: { type: 'bar', stacked: true, height: 320, toolbar: { show: false } },
          xaxis: { categories: stacked.categories },
          plotOptions: { bar: { columnWidth: '50%', borderRadius: 4 } },
          legend: { position: 'top', horizontalAlign: 'right' },
          dataLabels: {
            enabled: false
          },
          yaxis: {
            title: {
              text: 'tCO2e',
              style: { color: '#747677ff', fontSize: '12px', fontWeight: 600 }
            },
            labels: {
              formatter: (val: string) => {
                const num = Number(val);
                return num.toLocaleString('de-DE', { 
                  minimumFractionDigits: 0, 
                  maximumFractionDigits: 1 
                });
              }
            }
          },
          tooltip: {
            y: {
              formatter: (val: number) => {
                return `${val.toLocaleString('de-DE', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })} tCO2e`;
              }
            }
          }
        };

        const monthlySeriesWithColors = (monthly.series || []).map((s: any, i: number) => ({
          ...s,
          color: DASHBOARD_COLORS[i % DASHBOARD_COLORS.length]
        }));

        this.monthlyOptions = {
          series: monthlySeriesWithColors,
          colors: DASHBOARD_COLORS,
          chart: { type: 'line', height: 320, toolbar: { show: false } },
          stroke: { curve: 'straight', width: 3 },
          markers: { size: 5 },
          xaxis: { 
            categories: monthly.categories,
            labels: {
              style: { colors: '#747677ff', fontSize: '11px', fontWeight: 600 }
            }
          },
          yaxis: {
            title: {
              text: 'tCO2e',
              style: { color: '#747677ff', fontSize: '12px', fontWeight: 600 }
            },
            labels: {
              formatter: (val: number) => {
                return val.toLocaleString('de-DE', { 
                  minimumFractionDigits: 0, 
                  maximumFractionDigits: 0 
                });
              }
            }
          },
          dataLabels: { enabled: false },
          legend: { position: 'top', horizontalAlign: 'right' },
          tooltip: {
            y: {
              formatter: (val: number) => {
                return `${val.toLocaleString('de-DE', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })} tCO2e`;
              }
            }
          }
        };

        this.loading = false;
        this.errorMessage = null;
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = 'Error cargando los datos. Por favor, comuníquese con soporte.';
      }
    });
  }
}